import { useEffect, useState } from 'react';
import { Card } from './ui/card';
import Axios from '../Axios';
import { capitalize } from '../utils/capitalize';
import Loader from "../components/loader";
import "./HistoryPage.css";

const formatTradeType = (type) => {
  const map = { binary: 'Binary', spinAndWin: 'Spin And Win' };
  return map[type] ?? type;
};

const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return '-';
  const date = new Date(dateTimeString);
  if (isNaN(date.getTime())) return '-';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const amPmHours = Number(hours) >= 12 ? "PM" : "AM";
  return `${day}-${month}-${year} / ${hours}:${minutes}:${seconds} ${amPmHours}`;
};

function formatSignedCurrency(value) {
  const formatted = Math.abs(value).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4
  });
  if (value > 0) {
    return `+${formatted}`;
  }
  if (value < 0) {
    return `-${formatted}`;
  }
  return formatted;
}

export function HistoryPage() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [tradeHistory, setTradeHistory] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [wins, setWins] = useState(0);
  const [losses, setLosses] = useState(0);
  const [totalProfit, setTotalProfit] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);

  const getTradeHistory = async () => {
    try {
      setIsLoading(true);

      const { data } = await Axios.post(`/get-sportsdey-trade-history?page=${page}&limit=10`,
        {
          status: activeFilter === "win" ? "won" : activeFilter === "loss" ? "loss" : activeFilter === "draw" ? "draw" : "",
          type: "high_low",
          role: localStorage.getItem("tradeMode")
        });
      if (data?.success) {
        setTotalCount(data?.total ?? 0);
        setTradeHistory(
          data.result.map((item) => ({
            _id: item._id,
            id: item.order_id,
            dateTime: item.startTime,
            entry: item.start_price,
            exit: item.end_price,
            pair: item.pair?.replace(/_/g, '/') ?? '-',
            type: formatTradeType(item.type),
            direction: item.direction ?? '-',
            amount: `$${Number(item.amount).toFixed(2)}`,
            payout: item.result ? `$${Number(item.received_amount).toFixed(2)}` : '$0.00',
            profit: Number(item?.profit) || 0,
            result: item.result === 'won' ? 'Win' : item.result === 'loss' ? 'Loss' : 'Draw'
          }))
        );
        setWins(data?.wins ?? 0);
        setLosses(data?.losses ?? 0);
        setTotalProfit(data?.total_profits ?? 0);
      } else {
        setTradeHistory([]);
        setTotalCount(0);
      }
    } catch (error) {
      setTradeHistory([]);
      setTotalCount(0);
      console.error('Error fetching trade history:', error);
    } finally {
      setIsLoading(false);
      setIsLoaded(true);
    }
  };

  useEffect(() => {
    getTradeHistory();
  }, [page, activeFilter]);

  return (
    <section className={`history-page-pro h-full overflow-y-auto pb-8 ${isLoaded && !isLoading ? 'is-page-ready' : 'page-is-loading'}`}>
      {/* ──────────────────────────────────────────────────────────────────────────
          1. PAGE HEADER
          ────────────────────────────────────────────────────────────────────────── */}
      <header className="history-page-header">
        <h1>Trade History</h1>
        <p>Review your past trades</p>
      </header>

      {/* ──────────────────────────────────────────────────────────────────────────
          2. FOUR SUMMARY STAT CARDS (Total Trades, Wins, Losses, Total Profit)
          ────────────────────────────────────────────────────────────────────────── */}
      <section className="history-cards-grid">
        <Card as="article" className="history-card-pro">
          <div className="card-title-text">Total Trades</div>
          <div className="card-value-display">
            {totalCount || tradeHistory.length}
          </div>
        </Card>

        <Card as="article" className="history-card-pro">
          <div className="card-title-text">Wins</div>
          <div className="card-value-display val-win">
            {wins}
          </div>
        </Card>

        <Card as="article" className="history-card-pro">
          <div className="card-title-text">Losses</div>
          <div className="card-value-display val-loss">
            {losses}
          </div>
        </Card>

        <Card as="article" className="history-card-pro">
          <div className="card-title-text">Total Profit</div>
          <div className="card-value-display val-profit">
            {formatSignedCurrency(totalProfit)}
          </div>
        </Card>
      </section>

      {/* ──────────────────────────────────────────────────────────────────────────
          3. RESULT FILTER BUTTONS
          ────────────────────────────────────────────────────────────────────────── */}
      <div className="history-filters-bar" role="group" aria-label="Trade result filters">
        <button
          type="button"
          className={`history-filter-btn ${activeFilter === 'all' ? 'is-active' : ''}`}
          onClick={() => {
            setActiveFilter('all');
            setPage(1);
          }}
        >
          All
        </button>
        <button
          type="button"
          className={`history-filter-btn ${activeFilter === 'win' ? 'is-active' : ''}`}
          onClick={() => {
            setActiveFilter('win');
            setPage(1);
          }}
        >
          Win
        </button>
        <button
          type="button"
          className={`history-filter-btn ${activeFilter === 'loss' ? 'is-active' : ''}`}
          onClick={() => {
            setActiveFilter('loss');
            setPage(1);
          }}
        >
          Lose
        </button>
        <button
          type="button"
          className={`history-filter-btn ${activeFilter === 'draw' ? 'is-active' : ''}`}
          onClick={() => {
            setActiveFilter('draw');
            setPage(1);
          }}
        >
          Draw
        </button>
      </div>

      {/* ──────────────────────────────────────────────────────────────────────────
          4. PRO TRADE HISTORY TABLE
          ────────────────────────────────────────────────────────────────────────── */}
      <section className="history-table-card">
        {isLoading && (
          <div className="history-loader-overlay">
            <Loader isLoading={true} />
          </div>
        )}

        <div className="history-table-scroll">
          <table className="history-pro-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Pair</th>
                <th>Direction</th>
                <th>Entry / Exit</th>
                <th>Amount</th>
                <th>Payout</th>
                <th>Profit</th>
                <th>Result</th>
                <th>Date &amp; Time</th>
              </tr>
            </thead>
            <tbody>
              {tradeHistory?.length > 0 ? (
                tradeHistory.map((trade, index) => {
                  const isHigh =
                    String(trade.direction).toLowerCase() === 'up' ||
                    String(trade.direction).toLowerCase() === 'high';
                  return (
                    <tr
                      key={trade._id || `${trade.id}-${trade.dateTime}`}
                      className="history-row-animate"
                      style={{ animationDelay: `${index * 0.03}s` }}
                    >
                      <td>{(page - 1) * 10 + index + 1}</td>
                      <td style={{ fontWeight: 600, color: '#ffffff' }}>{trade.pair}</td>
                      <td>
                        <span className={isHigh ? 'direction-tag-up' : 'direction-tag-down'}>
                          {capitalize(trade.direction)}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontSize: '12px', color: '#e2e8f0' }}>{trade.entry || '--'}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>{trade.exit || '--'}</div>
                      </td>
                      <td style={{ fontWeight: 500, color: '#f8fafc' }}>{trade.amount}</td>
                      <td style={{ color: '#94a3b8' }}>{trade.payout}</td>
                      <td>
                        <span className={trade.result === 'Win' ? 'profit-val-win' : 'profit-val-loss'}>
                          {formatSignedCurrency(trade.profit)}
                        </span>
                      </td>
                      <td>
                        <span
                          className={
                            trade.result === 'Win'
                              ? 'result-tag-win'
                              : trade.result === 'Draw'
                              ? 'result-tag-draw'
                              : 'result-tag-loss'
                          }
                        >
                          {trade.result}
                        </span>
                      </td>
                      <td style={{ fontSize: '11px', color: '#7e8b9b' }}>
                        {trade.dateTime ? formatDateTime(trade.dateTime) : '-'}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={9} className="trades-empty-cell">
                    No trades found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar */}
        {totalCount > 10 && (
          <div className="history-pagination-bar">
            <button
              type="button"
              className="history-page-btn"
              disabled={page === 1}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            >
              &lt;
            </button>
            <span className="history-page-label">
              Page {page} of {Math.ceil(totalCount / 10)}
            </span>
            <button
              type="button"
              className="history-page-btn"
              disabled={page >= Math.ceil(totalCount / 10)}
              onClick={() => setPage((prev) => prev + 1)}
            >
              &gt;
            </button>
          </div>
        )}
      </section>
    </section>
  );
}
